import React, { useState, useEffect } from 'react';
import { useRouter, NextRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import useInterval from '@/hooks/useInterval';
import { openModal } from '@/store/actions/ui';
import { RootState } from '@/store/store';
import { getCookie, setCookie, setSession } from '@/utils/index';
import { IPopupTimerCmp } from './IPopupTimerCmp';
import { IOrderPopupTimer } from '@/types/index';
import { setCountDownBar } from '@/store/actions/countdownbar';

const isPopupToBeExcluded = (
  asPath: string,
  excludeUrlPaths: string[] | undefined,
  queryparameters: NextRouter['query'],
  excludeQueryParameters: { [k: string]: string }[] | undefined
): boolean => {
  let excludePopup = false;
  let urlPath = asPath;
  const index = asPath.indexOf('?');
  if (excludeUrlPaths) {
    if (index !== -1) {
      urlPath = asPath.substring(0, index);
    }
    excludePopup = excludeByPath(urlPath, excludeUrlPaths);
  }
  if (!excludePopup && excludeQueryParameters) {
    excludePopup = excludeByParams(queryparameters, excludeQueryParameters);
  }
  return excludePopup;
};

const excludeByPath = (urlPath: string, excludeUrlPaths: string[]): boolean => {
  let exclusionFound = excludeUrlPaths.find(excludePath => {
    if (!urlPath.includes(excludePath)) {
      return false;
    } else {
      return true;
    }
  });
  return exclusionFound ? true : false;
};

const excludeByParams = (
  queryParameters: NextRouter['query'],
  excludeQueryParameters: { [k: string]: string }[]
) => {
  let exclusionFound = excludeQueryParameters.find(excludeObj => {
    for (const excludeKey in excludeObj) {
      if (excludeObj[excludeKey] !== queryParameters[excludeKey]) {
        return false;
      }
    }
    return true;
  });

  return exclusionFound ? true : false;
};

const PopupTimer: React.FC<IPopupTimerCmp> = props => {
  const [open, setOpen] = useState(false);
  const [dispatchAction, setDispatchAction] = useState(false);
  const [callUseInterval, setCallUseInterval] = useState(true);
  const router = useRouter();
  let queryParameters = router?.query;

  const dispatch = useDispatch();
  const modalOpen = useSelector(
    (state: RootState) => state.uiReducer.modalOpen
  );

  const orderPopupTimer = useSelector(
    (state: RootState) => state.uiReducer.orderPopupTimer
  );

  const {
    content,
    delayUntilThePopupAppears,
    enable,
    minSessionDuration,
    numOfVisitedPages,
    bannerCountdown,
    promoCode,
    expiredCookieTime,
    excludeUrlParameters,
    excludeUrlPaths,
  } = props.fields;

  let popuptimer = null;

  const onClosePopup = () => {
    if (content === 'form_registration') {
      setCookie('isClosedPopupTimer_form_registration', '1', expiredCookieTime);
    }
    if (content === 'banner_countdown') {
      setCookie('isClosedPopupTimer_banner_countdown', '1', expiredCookieTime);
      setDispatchAction(true);
    }
    if (handleCalls()) {
      setCallUseInterval(false);
    }
    setOpen(false);
  };

  const handleCalls: () => boolean = () => {
    let isThereCookkie = false;
    isThereCookkie = orderPopupTimer.every((o: IOrderPopupTimer) => {
      if (getCookie(`isClosedPopupTimer_${o.content}`)) {
        return true;
      }
    });

    return isThereCookkie;
  };

  useEffect(() => {
    if (content === 'banner_countdown') {
      const promoBannerDuration = getCookie('promoBannerDuration');
      const isClosedPopupTimer = getCookie(
        'isClosedPopupTimer_banner_countdown'
      );

      if (
        bannerCountdown &&
        bannerCountdown.fields &&
        promoBannerDuration &&
        parseInt(promoBannerDuration, 10) > 0 &&
        isClosedPopupTimer == '1'
      ) {
        dispatch(
          setCountDownBar({
            code: promoCode || null,

            bannerCountdown: {
              ...bannerCountdown,
              fields: {
                ...bannerCountdown.fields,
                duration: parseInt(promoBannerDuration, 10),
              },
            },
          })
        );
      }
    }
  }, []);

  useEffect(() => {
    if (dispatchAction && bannerCountdown && bannerCountdown.fields) {
      dispatch(
        setCountDownBar({
          code: promoCode || null,
          bannerCountdown: {
            ...bannerCountdown,
            fields: {
              ...bannerCountdown.fields,
              duration: bannerCountdown.fields.duration
                ? bannerCountdown.fields.duration * 60 * 1000
                : undefined,
            },
          },
        })
      );
    }
  }, [dispatchAction]);

  useInterval(
    () => {
      if (!handleCalls()) {
        setSessionHandler();
      }
    },
    callUseInterval ? 30000 : null
  );

  useEffect(() => {
    if (!handleCalls()) {
      setSessionHandler();
    } else {
      setCallUseInterval(false);
    }
  }, [router]);

  const setSessionHandler = () => {
    let gaSessionIdCookie = '_gasessionid';
    let gaSessionDuration = '_gasessionduration';
    let gaPageViews = '_gapageviews';
    // let isPromoBannerVisibile = getCookie('isPromoBannerVisibile');

    setSession(gaSessionIdCookie, gaSessionDuration, gaPageViews);

    if (getCookie('_gasessionduration') && getCookie('_gapageviews')) {
      let gaSessionDuration = getCookie('_gasessionduration');
      let gaPageViews = getCookie('_gapageviews');

      let isClosed = `isClosedPopupTimer_${content}`;
      let isClosedPopupTimer = getCookie(isClosed);

      if (
        parseInt(gaSessionDuration, 10) >=
          minSessionDuration * 60 * 1000 - delayUntilThePopupAppears * 1000 &&
        parseInt(gaPageViews, 10) >= numOfVisitedPages &&
        isClosedPopupTimer == '' &&
        enable
      ) {
        setTimeout(() => {
          setOpen(!open);
        }, delayUntilThePopupAppears * 1000);
      }
    }
  };

  useEffect(() => {
    if (orderPopupTimer && orderPopupTimer.length > 0) {
      orderPopupTimer.forEach((popup: IOrderPopupTimer) => {
        if (open && !modalOpen && content === popup.content) {
          let exclude = isPopupToBeExcluded(
            router.asPath,
            excludeUrlPaths,
            queryParameters,
            excludeUrlParameters?.queryParameters
          );

          if (!exclude) {
            if (popup.content === 'banner_countdown') {
              dispatch(openModal('popuptimer', '', undefined, onClosePopup));
            }
            if (popup.content === 'form_registration') {
              dispatch(
                openModal('register', 'popupTimer', undefined, onClosePopup)
              );
            }
          }
        }
      });
    }
  }, [open]);

  return popuptimer;
};

export default PopupTimer;
